import React, { useState, useRef, useEffect } from 'react'; // useRef aur useEffect add karo
import { ChevronLeft, ChevronRight, Lightbulb, Target, DollarSign, Users, AlertTriangle, Rocket, TrendingUp, Zap, Map, ArrowRight, Copy, Download, X, Home } from 'lucide-react';
import { FaCircleArrowLeft } from "react-icons/fa6";
import './New.css';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useAuthCheck } from '../hooks/useAuthCheck';

// Add this custom popup component inside New.jsx (before the main component)
const CustomPopup = ({ message, type, onClose, onConfirm, showConfirm }) => {
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
        }} onClick={onClose}>
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: 'clamp(20px, 5vw, 32px)',
                maxWidth: 'min(400px, 90vw)',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideIn 0.3s ease',
                textAlign: 'center'
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{
                    fontSize: 'clamp(40px, 8vw, 60px)',
                    marginBottom: '16px'
                }}>
                    {type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}
                </div>
                <h4 style={{
                    color: type === 'error' ? '#dc3545' : type === 'warning' ? '#f59e0b' : '#0d53bd',
                    marginBottom: '16px',
                    fontSize: 'clamp(16px, 4vw, 20px)',
                    fontWeight: '600'
                }}>
                    {type === 'error' ? 'Oops!' : type === 'warning' ? 'Warning' : type === 'success' ? 'Success!' : 'Notice'}
                </h4>
                <p style={{
                    color: '#555',
                    marginBottom: '24px',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    lineHeight: '1.6'
                }}>
                    {message}
                </p>
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center'
                }}>
                    {showConfirm ? (
                        <>
                            <button
                                onClick={onConfirm}
                                style={{
                                    background: 'linear-gradient(135deg, #0d53bd, #06b6d4)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '30px',
                                    padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)',
                                    fontSize: 'clamp(14px, 3vw, 16px)',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    flex: 1
                                }}
                                onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 20px rgba(13, 83, 189, 0.3)'; }}
                                onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
                            >
                                Yes
                            </button>
                            <button
                                onClick={onClose}
                                style={{
                                    background: '#f3f4f6',
                                    color: '#666',
                                    border: 'none',
                                    borderRadius: '30px',
                                    padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)',
                                    fontSize: 'clamp(14px, 3vw, 16px)',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    flex: 1
                                }}
                                onMouseEnter={(e) => { e.target.style.background = '#e5e7eb'; }}
                                onMouseLeave={(e) => { e.target.style.background = '#f3f4f6'; }}
                            >
                                No
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onClose}
                            style={{
                                background: 'linear-gradient(135deg, #0d53bd, #06b6d4)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '30px',
                                padding: 'clamp(10px, 2vw, 12px) clamp(24px, 5vw, 40px)',
                                fontSize: 'clamp(14px, 3vw, 16px)',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                width: '100%'
                            }}
                            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 20px rgba(13, 83, 189, 0.3)'; }}
                            onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
                        >
                            OK
                        </button>
                    )}
                </div>
            </div>
            <style>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

function New() {
    useAuthCheck();
    
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({
        idea: '',
        industry: '',
        audience: '',
        problem: '',
        budget: '',
        timeline: ''
    });
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [copied, setCopied] = useState(false);

    // Refs banaye for input fields
    const industryOtherRef = useRef(null);
    const budgetOtherRef = useRef(null);
    const timelineOtherRef = useRef(null);

    const [popup, setPopup] = useState({
        show: false,
        message: '',
        type: 'info', // 'success', 'error', 'warning', 'info'
        onConfirm: null,
        showConfirm: false
    });

    // Helper function to show popup
    const showPopup = (message, type = 'info', showConfirm = false, onConfirm = null) => {
        setPopup({
            show: true,
            message,
            type,
            showConfirm,
            onConfirm
        });
    };

    const hidePopup = () => {
        setPopup(prev => ({ ...prev, show: false }));
    };

    
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }, []);

    // Jab "Other" select ho to automatically focus karne ke liye
    useEffect(() => {
        if (form.industry === 'other' && industryOtherRef.current) {
            industryOtherRef.current.focus();
        }
    }, [form.industry]);

    useEffect(() => {
        if (form.budget === 'other' && budgetOtherRef.current) {
            budgetOtherRef.current.focus();
        }
    }, [form.budget]);

    useEffect(() => {
        if (form.timeline === 'other' && timelineOtherRef.current) {
            timelineOtherRef.current.focus();
        }
    }, [form.timeline]);

    const steps = [
        { title: "Idea Basics", icon: <Lightbulb size={18} /> },
        { title: "Market Details", icon: <Target size={18} /> },
        { title: "Financial Info", icon: <DollarSign size={18} /> },
        { title: "Review & Analyze", icon: <Rocket size={18} /> }
    ];

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const nextStep = () => setStep((s) => Math.min(s + 1, steps.length - 1));
    const prevStep = () => setStep((s) => Math.max(s - 1, 0));

    // Copy to clipboard function
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    // Download as PDF function
    const downloadAsPDF = (sectionTitle, content) => {
        const element = document.createElement('div');
        element.innerHTML = `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
                <h2 style="color: #333; border-bottom: 2px solid #007bff; padding-bottom: 10px;">${sectionTitle}</h2>
                <p style="color: #555; line-height: 1.6; font-size: 14px; white-space: pre-wrap;">${content}</p>
                <div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; color: #777; font-size: 12px;">
                    Generated by InsightDraft AI • ${new Date().toLocaleDateString()}
                </div>
            </div>
        `;

        const opt = {
            margin: 1,
            filename: `${sectionTitle.replace(/\s+/g, '_')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };

        // html2pdf library use karo (pehle install karo: npm install html2pdf.js)
        import('html2pdf.js').then((html2pdf) => {
            html2pdf.default().from(element).set(opt).save();
        });
    };

    // Backend integration ke liye ready function
    const generateAnalysis = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('token');

            // ✅ Credit check - pehle check karo credits available hain ya nahi
            const user = localStorage.getItem('user');
            if (user) {
                try {
                    const userData = JSON.parse(user);
                    const credits = userData.credits || 0;

                    if (credits <= 0) {
                        showPopup('You have no credits remaining. Please purchase more credits to continue.', 'error');
                        setLoading(false);
                        return;
                    }
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            } else {
                // Guest user - check localStorage credits
                const guestCredits = localStorage.getItem('guest_credits');
                const creditsLeft = guestCredits ? parseInt(guestCredits) : 2;

                if (creditsLeft <= 0) {
                    alert('You have used all your free credits. Please login or sign up to get more credits!');
                    setLoading(false);
                    return;
                }
            }

            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch('https://insightdraft-backend.onrender.com/api/analyze-business-idea', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(form)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    // ✅ Update credits after successful analysis
                    updateCreditsAfterAnalysis(token);

                    // Save analysis data to localStorage
                    localStorage.setItem('newBusinessAnalysis', JSON.stringify(data.analysis));
                    localStorage.setItem('newBusinessFormData', JSON.stringify(form));
                    localStorage.setItem('newBusinessAnalysisTimestamp', Date.now().toString());
                    localStorage.setItem('analysisType', 'new');

                    // Redirect to analysis overview page WITH TYPE
                    window.location.href = '/analysis?type=new';
                } else {
                    console.error('API returned error:', data.error);

                    // Check if error is due to no credits
                    if (data.error === 'No credits remaining' || data.code === 'NO_CREDITS') {
                        showPopup('You have no credits remaining. Please purchase more credits to continue.', 'error');
                    } else {
                        // Fallback to mock data (only for guest users or if credits available)
                        const userData = user ? JSON.parse(user) : null;
                        const guestCredits = localStorage.getItem('guest_credits');
                        const creditsLeft = userData ? userData.credits : (guestCredits ? parseInt(guestCredits) : 2);

                        if (creditsLeft > 0) {
                            fallbackToMockAnalysis();
                        }
                    }
                }
            } else if (response.status === 403) {
                // Forbidden - likely no credits
                alert('No credits remaining. Please purchase more credits to continue.');
            } else {
                console.error('API response not OK:', response.status);

                // Only use mock data if guest has credits
                const guestCredits = localStorage.getItem('guest_credits');
                const creditsLeft = guestCredits ? parseInt(guestCredits) : 2;

                if (creditsLeft > 0) {
                    fallbackToMockAnalysis();
                }
            }
        } catch (error) {
            console.error('API Error:', error);

            // Check credits before fallback
            const guestCredits = localStorage.getItem('guest_credits');
            const creditsLeft = guestCredits ? parseInt(guestCredits) : 2;

            if (creditsLeft > 0) {
                fallbackToMockAnalysis();
            }
        } finally {
            setLoading(false);
        }
    };

    // ✅ Helper function to update credits after successful analysis
    const updateCreditsAfterAnalysis = (token) => {
        const user = localStorage.getItem('user');

        if (user) {
            try {
                const userData = JSON.parse(user);
                userData.credits = Math.max(0, (userData.credits || 0) - 1);
                localStorage.setItem('user', JSON.stringify(userData));

                // Trigger storage event for navbar update
                window.dispatchEvent(new Event('storage'));
            } catch (e) {
                console.error('Error updating user credits:', e);
            }
        } else {
            // Guest user - update guest credits
            const guestCredits = localStorage.getItem('guest_credits');
            const currentCredits = guestCredits ? parseInt(guestCredits) : 2;
            const newCredits = Math.max(0, currentCredits - 1);
            localStorage.setItem('guest_credits', newCredits.toString());

            // If guest credits become 0, show login prompt
            if (newCredits === 0) {
                setTimeout(() => {
                    const shouldLogin = window.confirm('You have used all your free credits! Would you like to login or sign up to get 5 more credits?');
                    if (shouldLogin) {
                        window.location.href = '/login';
                    }
                }, 1000);
            }
        }
    };

    // ✅ Renamed from useMockAnalysis to fallbackToMockAnalysis
    // ✅ Renamed from useMockAnalysis to fallbackToMockAnalysis
    const fallbackToMockAnalysis = () => {
        // Check credits before using mock
        const user = localStorage.getItem('user');
        const guestCredits = localStorage.getItem('guest_credits');

        let creditsLeft;
        if (user) {
            try {
                const userData = JSON.parse(user);
                creditsLeft = userData.credits || 0;
            } catch {
                creditsLeft = 0;
            }
        } else {
            creditsLeft = guestCredits ? parseInt(guestCredits) : 2;
        }

        if (creditsLeft <= 0) {
            alert('No credits remaining. Please login or purchase more credits.');
            return;
        }

        const mockAnalysis = generateMockAnalysis();
        localStorage.setItem('newBusinessAnalysis', JSON.stringify(mockAnalysis));
        localStorage.setItem('newBusinessFormData', JSON.stringify(form));
        localStorage.setItem('newBusinessAnalysisTimestamp', Date.now().toString());
        localStorage.setItem('analysisType', 'new');

        // Update credits
        updateCreditsAfterAnalysis();

        window.location.href = '/analysis?type=new';
    };

    // Mock data generator - Backend se replace kar dena
    const generateMockAnalysis = () => {
        return {
            marketAnalysis: "High growth potential in AI meal planning sector with 200% YoY growth. Target market size: $2.4B with 15M potential users. Key trends include personalized nutrition, mobile-first solutions, and integration with fitness trackers. Market is expected to grow at 25% CAGR over next 5 years.",
            audienceResearch: "Primary: Health-conscious professionals (25-40) with disposable income. Secondary: Fitness enthusiasts tracking macros. Tertiary: Busy parents seeking convenience. Key insights: Willing to pay $15-30/month for personalized solutions, value time-saving features, prefer mobile apps over web.",
            requiredResources: "Core Team: AI developers (2), Nutritionist, Mobile app developer (iOS/Android), UI/UX designer. Infrastructure: Cloud servers (AWS/Azure), Database, API integrations. Tools: Project management, Analytics, CRM. Marketing: Content creator, Digital ads budget.",
            costEstimation: "Initial Setup: $45K (Development: $30K, Legal: $5K, Marketing: $10K). Monthly Operational: $8K (Salaries: $6K, Infrastructure: $1K, Marketing: $1K). Break-even: 14 months at 1,000 subscribers. Year 1 Revenue Projection: $320K at 2,000 subscribers.",
            competitorBreakdown: "MealPal (35% market share) - Strength: Brand recognition. Weakness: Limited personalization. FitFood (25%) - Strength: Quality ingredients. Weakness: High price. Freshly (20%) - Strength: Convenience. Weakness: No AI features. Gap: AI-powered personalized nutrition missing.",
            executionStrategy: "Phase 1 (Months 1-3): MVP Development - Core AI algorithm, Basic mobile app, 50 beta testers. Phase 2 (Months 4-5): User Testing - Feedback collection, Feature refinement, Bug fixes. Phase 3 (Months 6-9): Scale Launch - Marketing push, App store optimization, Partnership development.",
            uniqueSellingPoints: "1. AI-powered personalization adapting to dietary preferences and goals. 2. Real-time nutrition tracking with barcode scanning. 3. Integration with popular fitness apps and wearables. 4. Family meal planning for multiple dietary needs. 5. Grocery delivery partnerships for one-click ordering.",
            riskAnalysis: "Competition Risk: Medium - Established players but none offer AI personalization. Regulatory Risk: Low - Basic food safety compliance needed. Technical Risk: Medium - AI algorithm accuracy critical. Market Risk: Low - Growing health consciousness. Mitigation: Start with niche market, focus on unique AI features.",
            monetizationPlan: "Freemium Model: Basic features free (meal ideas, basic tracking). Premium Tier: $9.99/month (AI personalization, advanced analytics). Family Plan: $19.99/month (up to 5 users). Corporate Wellness: Custom pricing for companies. Revenue Streams: Subscriptions (70%), Partnerships (20%), In-app purchases (10%).",
            roadmap: "Q1 2024: Prototype development and seed funding. Q2 2024: Beta launch with 500 users. Q3 2024: Public launch targeting 5,000 users. Q4 2024: Feature expansion and partnership development. Q1 2025: Mobile app optimization and user growth to 15,000. Q2 2025: International expansion planning.",
            growthStrategy: "Phase 1: Content marketing through health blogs and social media. Phase 2: App store optimization and SEO for meal planning keywords. Phase 3: Fitness influencer partnerships and referral programs. Phase 4: Corporate wellness programs and healthcare partnerships. Phase 5: International market entry with localized content.",
            chatSupport: "24/7 AI chat support for instant answers. Dedicated business strategist for complex queries. Weekly group coaching sessions. Priority email support for premium users. Community forum for peer learning and idea exchange."
        };
    };

    const analysisSections = [
        {
            title: "Deep Market Analysis",
            icon: <TrendingUp size={20} />,
            key: "marketAnalysis",
            color: "blue"
        },
        {
            title: "Target Audience Research",
            icon: <Users size={20} />,
            key: "audienceResearch",
            color: "green"
        },
        {
            title: "Required Resources",
            icon: <Zap size={20} />,
            key: "requiredResources",
            color: "yellow"
        },
        {
            title: "Cost Estimation",
            icon: <DollarSign size={20} />,
            key: "costEstimation",
            color: "emerald"
        },
        {
            title: "Competitor Breakdown",
            icon: <Target size={20} />,
            key: "competitorBreakdown",
            color: "red"
        },
        {
            title: "Execution Strategy",
            icon: <Rocket size={20} />,
            key: "executionStrategy",
            color: "purple"
        },
        {
            title: "Unique Selling Points",
            icon: <Lightbulb size={20} />,
            key: "uniqueSellingPoints",
            color: "indigo"
        },
        {
            title: "Risk Analysis",
            icon: <AlertTriangle size={20} />,
            key: "riskAnalysis",
            color: "orange"
        },
        {
            title: "Monetization Plan",
            icon: <DollarSign size={20} />,
            key: "monetizationPlan",
            color: "teal"
        },
        {
            title: "Full Roadmap",
            icon: <Map size={20} />,
            key: "roadmap",
            color: "pink"
        },
        {
            title: "Business Growth Strategy",
            icon: <TrendingUp size={20} />,
            key: "growthStrategy",
            color: "cyan"
        },
        {
            title: "Chat About Your Idea",
            icon: <Users size={20} />,
            key: "chatSupport",
            color: "gray"
        }
    ];

    const getColorClasses = (color) => {
        const colors = {
            blue: {
                border: 'border-blue-200',
                bg: 'bg-blue-50',
                hover: 'hover:bg-blue-100',
                text: 'text-blue-700',
                button: 'btn-outline-primary',
                cardbg: '#dbeafe',
                cardborder: '#93c5fd',
                cardtext: '#1e40af' // blue-800
            },
            green: {
                border: 'border-green-200',
                bg: 'bg-green-50',
                hover: 'hover:bg-green-100',
                text: 'text-green-700',
                button: 'btn-outline-success',
                cardbg: '#dcfce7',
                cardborder: '#86efac',
                cardtext: '#166534'
            },
            yellow: {
                border: 'border-yellow-200',
                bg: 'bg-yellow-50',
                hover: 'hover:bg-yellow-100',
                text: 'text-yellow-700',
                button: 'btn-outline-warning',
                cardbg: '#fef9c3',
                cardborder: '#fde047',
                cardtext: '#854d0e'
            },
            emerald: {
                border: 'border-success-200',
                bg: 'bg-success-50',
                hover: 'hover:bg-success-100',
                text: 'text-success-700',
                button: 'btn-outline-success',
                cardbg: '#d1fae5',
                cardborder: '#6ee7b7',
                cardtext: '#065f46'
            },
            red: {
                border: 'border-red-200',
                bg: 'bg-red-50',
                hover: 'hover:bg-red-100',
                text: 'text-red-700',
                button: 'btn-outline-danger',
                cardbg: '#fee2e2',
                cardborder: '#fca5a5',
                cardtext: '#991b1b'
            },
            purple: {
                border: 'border-purple-200',
                bg: 'bg-purple-50',
                hover: 'hover:bg-purple-100',
                text: 'text-purple-700',
                button: 'btn-outline-primary',
                cardbg: '#f3e8ff',
                cardborder: '#c4b5fd',
                cardtext: '#6b21a8'
            },
            indigo: {
                border: 'border-indigo-200',
                bg: 'bg-indigo-50',
                hover: 'hover:bg-indigo-100',
                text: 'text-indigo-700',
                button: 'btn-outline-primary',
                cardbg: '#e0e7ff',
                cardborder: '#a5b4fc',
                cardtext: '#3730a3'
            },
            orange: {
                border: 'border-warning-200',
                bg: 'bg-warning-50',
                hover: 'hover:bg-warning-100',
                text: 'text-warning-700',
                button: 'btn-outline-warning',
                cardbg: '#ffedd5',
                cardborder: '#fdba74',
                cardtext: '#9a3412'
            },
            teal: {
                border: 'border-info-200',
                bg: 'bg-info-50',
                hover: 'hover:bg-info-100',
                text: 'text-info-700',
                button: 'btn-outline-info',
                cardbg: '#ccfbf1',
                cardborder: '#5eead4',
                cardtext: '#134e4a'
            },
            pink: {
                border: 'border-pink-200',
                bg: 'bg-pink-50',
                hover: 'hover:bg-pink-100',
                text: 'text-pink-700',
                button: 'btn-outline-danger',
                cardbg: '#fce7f3',
                cardborder: '#f9a8d4',
                cardtext: '#831843'
            },
            cyan: {
                border: 'border-info-200',
                bg: 'bg-info-50',
                hover: 'hover:bg-info-100',
                text: 'text-info-700',
                button: 'btn-outline-info',
                cardbg: '#cffafe',
                cardborder: '#5eead4',
                cardtext: '#164e63'
            },
            gray: {
                border: 'border-secondary-200',
                bg: 'bg-secondary-50',
                hover: 'hover:bg-secondary-100',
                text: 'text-secondary-700',
                button: 'btn-outline-secondary',
                cardbg: '#f3f4f6',
                cardborder: '#d1d5db',
                cardtext: '#374151'
            }
        };
        return colors[color] || colors.gray;
    };

    const SectionCard = ({ section, analysis }) => {
        const colorClass = getColorClasses(section.color);
        const content = analysis[section.key];

        // Responsive preview text
        const getPreview = () => {
            if (!content) return 'No content available...';
            const firstLine = content.split('\n')[0];
            // Screen size के हिसाब से different preview length
            const previewLength = window.innerWidth < 768 ? 60 : 100;
            return firstLine.substring(0, previewLength) + (firstLine.length > previewLength ? '...' : '');
        };

        const preview = getPreview();

        const handleCardClick = () => {
            // Save data to localStorage for CategoryPageNew
            localStorage.setItem('newBusinessAnalysis', JSON.stringify(analysis));
            localStorage.setItem('newBusinessFormData', JSON.stringify(form));

            // Navigate to category page
            window.location.href = `/category-new/${section.key}`;
        };

        return (
            <div
                className={`card h-100 border-2 ${colorClass.cardborder} transition-all cursor-pointer`}
                style={{
                    borderRadius: '16px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    minHeight: '180px',
                    maxHeight: '250px'
                }}
                onClick={handleCardClick} // Change to handleCardClick
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
            >
                <div className="card-body d-flex flex-column p-2 p-sm-3 p-md-4"
                    style={{ height: '100%' }}>
                    {/* Icon and Title - Responsive */}
                    <div className="d-flex align-items-center mb-2 mb-sm-3">
                        <div className={`rounded-circle p-1 p-sm-2 ${colorClass.bg} ${colorClass.text} me-2 me-sm-3 flex-shrink-0`}
                            style={{
                                width: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                            {React.cloneElement(section.icon, {
                                size: window.innerWidth < 768 ? 16 : 18
                            })}
                        </div>
                        <h6 className={`fw-bold mb-0 ${colorClass.text}`}
                            style={{
                                fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                                lineHeight: 1.3,
                                wordBreak: 'break-word'
                            }}>
                            {section.title}
                        </h6>
                    </div>

                    {/* Preview Text - Responsive */}
                    <p className="text-muted mb-2 mb-sm-3 flex-grow-1"
                        style={{
                            lineHeight: '1.5',
                            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical'
                        }}>
                        {preview}
                    </p>

                    {/* Footer - Responsive */}
                    <div className="d-flex justify-content-between align-items-center mt-auto">
                        <small className={`${colorClass.text} fw-semibold`}
                            style={{ fontSize: '0.75rem' }}>
                            Click to view
                        </small>
                        <ArrowRight
                            size={window.innerWidth < 768 ? 14 : 16}
                            className={colorClass.text}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const SectionModal = ({ section, analysis, onClose }) => {
        if (!section) return null;

        const colorClass = getColorClasses(section.color);
        const content = analysis[section.key];

        return (
            <div className="modal show d-block" style={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                position: 'fixed',
                inset: 0,
                zIndex: 1050,
                overflow: 'hidden'
            }} onClick={onClose}>
                <div className="modal-dialog modal-dialog-centered m-0"
                    style={{
                        width: '100%',
                        height: '100%',
                        maxWidth: 'none',
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.5rem'
                    }}>
                    <div className="modal-content border-0" onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '100%',
                            maxWidth: '900px',
                            maxHeight: '95vh',
                            height: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                        }}>
                        {/* Header */}
                        <div className="modal-header border-0 pb-0 position-relative sticky-top bg-white"
                            style={{
                                flexShrink: 0,
                                padding: '1rem 1rem 0.5rem',
                                borderBottom: '1px solid rgba(0,0,0,0.1)'
                            }}>
                            {/* Close button - Top right corner */}
                            <button
                                type="button"
                                className="btn-close position-absolute"
                                onClick={onClose}
                                aria-label="Close"
                                style={{
                                    top: '1rem',
                                    right: '1rem',
                                    zIndex: 1
                                }}
                            ></button>

                            {/* Title section */}
                            <div className="d-flex align-items-center w-100 pe-4">
                                <div className={`rounded-circle p-2 ${colorClass.bg} ${colorClass.text} me-2 me-sm-3`}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                    {section.icon}
                                </div>
                                <h5 className={`modal-title fw-bold ${colorClass.text} mb-0`}
                                    style={{
                                        fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                                        lineHeight: 1.3,
                                        wordBreak: 'break-word'
                                    }}>
                                    {section.title}
                                </h5>
                            </div>
                        </div>

                        {/* Content - Scrollable area */}
                        <div className="modal-body p-0 d-flex flex-column" style={{ flexGrow: 1, overflow: 'hidden' }}>
                            <div className="p-2 p-sm-3 p-md-4 flex-grow-1" style={{ overflow: 'auto' }}>
                                <div className={`p-3 p-sm-4 rounded-3 ${colorClass.bg}`} style={{
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap',
                                    fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                }}>
                                    {content}
                                </div>
                            </div>

                            {/* Mobile Footer - Sticky at bottom */}
                            <div className="sticky-bottom bg-white py-2 border-top d-block d-sm-none mt-auto"
                                style={{
                                    flexShrink: 0,
                                    boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
                                }}>
                                <div className="container-fluid px-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        {/* Date */}
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {new Date().toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </small>

                                        {/* Action buttons */}
                                        <div className="d-flex gap-1">
                                            <button
                                                className="btn btn-primary d-flex align-items-center justify-content-center"
                                                onClick={() => copyToClipboard(content)}
                                                title="Copy"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                    padding: '0'
                                                }}
                                            >
                                                <Copy size={16} />
                                            </button>

                                            <button
                                                className="btn btn-success d-flex align-items-center justify-content-center"
                                                onClick={() => downloadAsPDF(section.title, content)}
                                                title="Download"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                    padding: '0'
                                                }}
                                            >
                                                <Download size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Desktop Footer */}
                            <div className="d-none d-sm-flex justify-content-between align-items-center p-3 border-top mt-auto">
                                <small className="text-muted">
                                    Generated: {new Date().toLocaleDateString()}
                                </small>

                                <div className="d-flex gap-2">
                                    <button
                                        className={`btn ${colorClass.button} d-flex align-items-center`}
                                        onClick={() => copyToClipboard(content)}
                                        style={{
                                            padding: '0.375rem 0.75rem',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <Copy size={14} className="me-1" />
                                        {copied ? 'Copied!' : 'Copy'}
                                    </button>

                                    <button
                                        className={`btn ${colorClass.button} d-flex align-items-center`}
                                        onClick={() => downloadAsPDF(section.title, content)}
                                        style={{
                                            padding: '0.375rem 0.75rem',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <Download size={14} className="me-1" />
                                        Download
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
            {popup.show && (
                <CustomPopup
                    message={popup.message}
                    type={popup.type}
                    onClose={hidePopup}
                    onConfirm={() => {
                        if (popup.onConfirm) popup.onConfirm();
                        hidePopup();
                    }}
                    showConfirm={popup.showConfirm}
                />
            )}
            <div className="container py-5" style={{ maxWidth: 1200 }}>
                {!analysis ? (
                    <>
                        <div className="container-fluid px-0 mb-4 mb-md-5">
                            <div className="row align-items-center g-2">
                                {/* Back Button Column */}
                                <div className="col-auto">
                                    <button
                                        className="btn btn-outline-primary d-flex align-items-center justify-content-center order-1 order-md-0"
                                        onClick={() => window.location.href = '/'}
                                        style={{
                                            borderRadius: '12px',
                                            padding: 'clamp(8px, 2vw, 10px) clamp(12px, 2.5vw, 20px)',
                                            fontSize: 'clamp(14px, 3vw, 16px)',
                                            height: 'clamp(40px, 8vw, 44px)',
                                            minWidth: 'clamp(40px, 10vw, 120px)',
                                            flexShrink: 0,
                                            borderWidth: '2px',
                                            transition: 'all 0.3s ease',
                                            alignSelf: 'flex-start'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#0d53bd';
                                            e.currentTarget.style.color = 'white';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = '#0d53bd';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        {/* XL Screens (≥1200px): Full text with icon */}
                                        <span className="d-none d-xl-inline-flex align-items-center">
                                            <FaCircleArrowLeft className="me-2" size={18} />
                                            Back to Home
                                        </span>

                                        {/* LG Screens (992px-1199px): Icon + "Back" */}
                                        <span className="d-none d-lg-inline-flex d-xl-none align-items-center">
                                            <FaCircleArrowLeft className="me-1" size={16} />
                                            Back
                                        </span>

                                        {/* MD Screens (768px-991px): Only icon with text */}
                                        <span className="d-none d-md-inline-flex d-lg-none align-items-center">
                                            <FaCircleArrowLeft size={16} />
                                            <span className="ms-1">Back</span>
                                        </span>

                                        {/* SM Screens (576px-767px): Only icon */}
                                        <span className="d-none d-sm-inline-flex d-md-none align-items-center justify-content-center"
                                            title="Back to Home">
                                            <FaCircleArrowLeft size={16} />
                                        </span>

                                        {/* XS Screens (<576px): Only icon, centered */}
                                        <span className="d-inline-flex d-sm-none align-items-center justify-content-center"
                                            title="Back to Home">
                                            <FaCircleArrowLeft size={16} />
                                        </span>
                                    </button>
                                </div>

                                {/* Title Column - Takes remaining space */}
                                <div className="col">
                                    <div className="text-center">
                                        <h1 className="fw-bold mb-1" style={{
                                            background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text',
                                            fontSize: 'clamp(1.3rem, 3.8vw, 1.9rem)',
                                            lineHeight: '1.2',
                                            wordBreak: 'break-word',
                                            padding: '0 10px'
                                        }}>
                                            New Idea Analyser
                                        </h1>

                                        <p className="text-muted m-0" style={{ fontSize: 'clamp(0.85rem, 1.6vw, 0.95rem)' }}>
                                            Transform your idea into a successful business with AI-powered insights
                                        </p>
                                    </div>
                                </div>

                                {/* Empty column for balance on larger screens */}
                                <div className="col-auto d-none d-lg-block" style={{ width: '140px' }}></div>
                            </div>
                        </div>

                        {/* Responsive Progress Bar */}
                        <div className="mb-4 mb-md-5 px-2" style={{
                            maxWidth: 'min(800px, 100%)',
                            margin: '0 auto'
                        }}>
                            {/* Steps with responsive spacing */}
                            <div className="d-flex justify-content-between align-items-start mb-3 position-relative">
                                {/* Connecting Line - Background */}
                                <div className="position-absolute top-0 start-0 end-0"
                                    style={{
                                        top: '20px',
                                        height: '3px',
                                        backgroundColor: '#e9ecef',
                                        margin: '0 20px',
                                        zIndex: 1
                                    }}>
                                </div>

                                {/* Connecting Line - Progress */}
                                <div className="position-absolute top-0 start-0"
                                    style={{
                                        top: '20px',
                                        height: '3px',
                                        width: `${(step / (steps.length - 1)) * 100}%`,
                                        background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                                        margin: '0 20px',
                                        zIndex: 2,
                                        transition: 'width 0.4s ease'
                                    }}>
                                </div>

                                {steps.map((stepItem, idx) => (
                                    <div key={idx} className="text-center position-relative z-3" style={{ flex: 1 }}>
                                        {/* Step Circle with Icon */}
                                        <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${idx <= step ? 'bg-primary' : 'bg-light'}`}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                border: '3px solid white',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                position: 'relative'
                                            }}>
                                            {/* Completed Step - Checkmark */}
                                            {idx < step ? (
                                                <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                                                    <path d="M1 7L6 12L17 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            ) : idx === step ? (
                                                <div style={{
                                                    animation: idx === step ? 'pulse 2s infinite' : 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {React.cloneElement(stepItem.icon, {
                                                        size: 20,
                                                        color: idx <= step ? '#ffffff' : '#6c757d'
                                                    })}
                                                </div>
                                            ) : (
                                                <div style={{
                                                    opacity: 0.6,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {React.cloneElement(stepItem.icon, {
                                                        size: 20,
                                                        color: '#6c757d'
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        {/* Step Title - Responsive */}
                                        <div className="mt-2 px-1">
                                            <div className="d-none d-md-block" style={{
                                                color: idx <= step ? '#0d53bd' : '#6c757d',
                                                fontSize: '0.85rem',
                                                fontWeight: idx <= step ? 600 : 500,
                                                lineHeight: 1.2
                                            }}>
                                                {stepItem.title}
                                            </div>
                                            <div className="d-block d-md-none" style={{
                                                color: idx <= step ? '#0d53bd' : '#6c757d',
                                                fontSize: '0.7rem',
                                                fontWeight: idx <= step ? 600 : 500
                                            }}>
                                                {stepItem.title}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Progress Percentage - Responsive */}
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                    Progress
                                </span>
                                <div className="text-end">
                                    <div style={{
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        fontWeight: 600,
                                        color: '#0d53bd'
                                    }}>
                                        {Math.round(((step + 1) / steps.length) * 100)}%
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                        Step {step + 1} of {steps.length}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step Forms */}
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <div className="card p-4 mb-4 border-0" style={{
                                    borderRadius: 20,
                                    boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    {step === 0 && (
                                        <div className="row g-4">
                                            <div className="col-12">
                                                <label className="form-label fw-semibold fs-6 text-primary">What's your brilliant idea? 💡</label>
                                                <input
                                                    className="form-control form-control-lg border-0 fs-6"
                                                    name="idea"
                                                    value={form.idea}
                                                    onChange={handleChange}
                                                    placeholder="e.g. AI-powered personalized meal planner for busy professionals"
                                                    style={{
                                                        background: '#f8f9fa',
                                                        borderRadius: 12,
                                                        border: '1px solid rgba(0,0,0,0.1)',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                                    onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold fs-6 text-primary">Industry Focus</label>
                                                <div className="position-relative">
                                                    {form.industry === 'other' ? (
                                                        <div className="d-flex align-items-center">
                                                            <input
                                                                ref={industryOtherRef} // Ref add karo
                                                                className="form-control form-control-lg border-0 fs-6"
                                                                name="industryOther"
                                                                value={form.industryOther || ''}
                                                                onChange={(e) => setForm({ ...form, industryOther: e.target.value })}
                                                                placeholder="Type your industry"
                                                                style={{
                                                                    background: '#f8f9fa',
                                                                    borderRadius: 12,
                                                                    padding: '12px 16px',
                                                                    paddingRight: '50px',
                                                                    border: '1px solid rgba(0,0,0,0.1)',
                                                                    transition: 'all 0.3s ease'
                                                                }}
                                                                onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                                                onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-link position-absolute end-0"
                                                                onClick={() => setForm({ ...form, industry: '', industryOther: '' })}
                                                                style={{
                                                                    padding: '8px 16px',
                                                                    color: '#666',
                                                                    textDecoration: 'none',
                                                                    fontSize: '1.5rem',
                                                                    transition: 'all 0.3s ease',
                                                                    zIndex: 2
                                                                }}
                                                                title="Back to options"
                                                                onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                                                onMouseLeave={(e) => e.target.style.color = '#666'}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 0 }}>
                                                            <InputLabel
                                                                id="industry-select-label"
                                                                sx={{
                                                                    color: '#6c757d',
                                                                    fontSize: '0.875rem',
                                                                    '&.Mui-focused': {
                                                                        color: '#0d53bd'
                                                                    },
                                                                    transform: 'translate(14px, 11.5px) scale(1)',
                                                                    '&.MuiInputLabel-shrink': {
                                                                        transform: 'translate(14px, -9px) scale(0.75)',
                                                                        backgroundColor: '#f8f9fa',
                                                                        padding: '0 4px'
                                                                    }
                                                                }}
                                                            >
                                                                Select your industry
                                                            </InputLabel>
                                                            <Select
                                                                labelId="industry-select-label"
                                                                id="industry-select"
                                                                name="industry"
                                                                value={form.industry || ''}
                                                                onChange={handleChange}
                                                                label="Select your industry"
                                                                sx={{
                                                                    borderRadius: '12px',
                                                                    background: '#f8f9fa',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'rgba(0,0,0,0.1)',
                                                                        borderRadius: '12px'
                                                                    },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#0d53bd'
                                                                    },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#0d53bd',
                                                                        borderWidth: '2px'
                                                                    },
                                                                    '& .MuiSelect-select': {
                                                                        padding: '15.5px 14px',
                                                                        fontSize: '0.875rem',
                                                                        display: 'flex',
                                                                    },
                                                                    '& .MuiSelect-icon': {
                                                                        right: '12px',
                                                                        color: '#666'
                                                                    }
                                                                }}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        sx: {
                                                                            borderRadius: '12px',
                                                                            marginTop: '8px',
                                                                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                                            maxHeight: 300,
                                                                            '&::-webkit-scrollbar': {
                                                                                width: '8px',
                                                                            },
                                                                            '&::-webkit-scrollbar-track': {
                                                                                background: '#f1f1f1',
                                                                                borderRadius: '4px',
                                                                            },
                                                                            '&::-webkit-scrollbar-thumb': {
                                                                                background: '#888',
                                                                                borderRadius: '4px',
                                                                                '&:hover': {
                                                                                    background: '#555'
                                                                                }
                                                                            },
                                                                            '& .MuiMenuItem-root': {
                                                                                fontSize: '0.875rem',
                                                                                padding: '10px 16px',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(13, 83, 189, 0.1)'
                                                                                },
                                                                                '&.Mui-selected': {
                                                                                    backgroundColor: '#0d53bd',
                                                                                    color: 'white',
                                                                                    '&:hover': {
                                                                                        backgroundColor: '#0d53bd'
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>None</em>
                                                                </MenuItem>
                                                                <MenuItem value="tech">Technology & SaaS</MenuItem>
                                                                <MenuItem value="food" >Food & Beverage</MenuItem>
                                                                <MenuItem value="health">Health & Wellness</MenuItem>
                                                                <MenuItem value="finance">Finance & Fintech</MenuItem>
                                                                <MenuItem value="education">Education & EdTech</MenuItem>
                                                                <MenuItem value="ecommerce">E-commerce & Retail</MenuItem>
                                                                <MenuItem value="entertainment">Entertainment & Media</MenuItem>
                                                                <MenuItem value="realestate">Real Estate & Property</MenuItem>
                                                                <MenuItem value="transportation">Transportation & Logistics</MenuItem>
                                                                <MenuItem value="manufacturing">Manufacturing</MenuItem>
                                                                <MenuItem value="agriculture">Agriculture & Farming</MenuItem>
                                                                <MenuItem value="hospitality">Hospitality & Tourism</MenuItem>
                                                                <MenuItem value="fashion">Fashion & Apparel</MenuItem>
                                                                <MenuItem value="beauty">Beauty & Cosmetics</MenuItem>
                                                                <MenuItem value="sports">Sports & Fitness</MenuItem>
                                                                <MenuItem value="automotive">Automotive</MenuItem>
                                                                <MenuItem value="energy">Energy & Utilities</MenuItem>
                                                                <MenuItem value="consulting">Consulting & Services</MenuItem>
                                                                <MenuItem value="nonprofit">Non-profit & Social Impact</MenuItem>
                                                                <MenuItem value="other">Other Industry</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {step === 1 && (
                                        <div className="row g-4">
                                            <div className="col-12">
                                                <label className="form-label fw-semibold fs-6 text-primary">Target Audience 🎯</label>
                                                <input
                                                    className="form-control form-control-lg border-0 fs-6"
                                                    name="audience"
                                                    value={form.audience}
                                                    onChange={handleChange}
                                                    placeholder="e.g. Health-conscious professionals aged 25-40 with busy lifestyles"
                                                    style={{
                                                        background: '#f8f9fa',
                                                        borderRadius: 12,
                                                        border: '1px solid rgba(0,0,0,0.1)',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                                    onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                                />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold fs-6 text-primary">Problem You're Solving 🔧</label>
                                                <textarea
                                                    className="form-control form-control-lg border-0 fs-6"
                                                    name="problem"
                                                    value={form.problem}
                                                    onChange={handleChange}
                                                    rows={4}
                                                    placeholder="Describe the specific pain point or challenge your idea addresses..."
                                                    style={{
                                                        background: '#f8f9fa',
                                                        borderRadius: 12,
                                                        border: '1px solid rgba(0,0,0,0.1)',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                                    onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {step === 2 && (
                                        <div className="row g-4">
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold fs-6 text-primary">Initial Budget 💰</label>
                                                <div className="position-relative">
                                                    {form.budget === 'other' ? (
                                                        <div className="d-flex align-items-center">
                                                            <input
                                                                ref={budgetOtherRef} // Ref add karo
                                                                className="form-control form-control-lg border-0 fs-6"
                                                                name="budgetOther"
                                                                value={form.budgetOther || ''}
                                                                onChange={(e) => setForm({ ...form, budgetOther: e.target.value })}
                                                                placeholder="e.g. ₹50K, ₹150K+"
                                                                style={{
                                                                    background: '#f8f9fa',
                                                                    borderRadius: 12,
                                                                    paddingRight: '50px',
                                                                    border: '1px solid rgba(0,0,0,0.1)',
                                                                    transition: 'all 0.3s ease'
                                                                }}
                                                                onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                                                onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-link position-absolute end-0"
                                                                onClick={() => setForm({ ...form, budget: '', budgetOther: '' })}
                                                                style={{
                                                                    padding: '8px 16px',
                                                                    color: '#666',
                                                                    textDecoration: 'none',
                                                                    fontSize: '1.5rem',
                                                                    transition: 'all 0.3s ease',
                                                                    zIndex: 2
                                                                }}
                                                                title="Back to options"
                                                                onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                                                onMouseLeave={(e) => e.target.style.color = '#666'}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <FormControl fullWidth variant="outlined" size="medium">
                                                            <InputLabel
                                                                id="budget-select-label"
                                                                sx={{
                                                                    color: '#6c757d',
                                                                    fontSize: '0.875rem',
                                                                    '&.Mui-focused': {
                                                                        color: '#0d53bd'
                                                                    }
                                                                }}
                                                            >
                                                                Select budget range
                                                            </InputLabel>
                                                            <Select
                                                                labelId="budget-select-label"
                                                                id="budget-select"
                                                                name="budget"
                                                                value={form.budget || ''}
                                                                onChange={handleChange}
                                                                label="Select budget range"
                                                                sx={{
                                                                    borderRadius: '12px',
                                                                    background: '#f8f9fa',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'rgba(0,0,0,0.1)',
                                                                        borderRadius: '12px'
                                                                    },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#0d53bd'
                                                                    },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#0d53bd',
                                                                        borderWidth: '2px'
                                                                    },
                                                                    '& .MuiSelect-select': {
                                                                        padding: '15.5px 14px',
                                                                        fontSize: '0.875rem',
                                                                        display: 'flex',
                                                                    }
                                                                }}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        sx: {
                                                                            borderRadius: '12px',
                                                                            marginTop: '8px',
                                                                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                                            maxHeight: 250,
                                                                            '&::-webkit-scrollbar': {
                                                                                width: '8px',
                                                                            },
                                                                            '&::-webkit-scrollbar-track': {
                                                                                background: '#f1f1f1',
                                                                                borderRadius: '4px',
                                                                            },
                                                                            '&::-webkit-scrollbar-thumb': {
                                                                                background: '#888',
                                                                                borderRadius: '4px',
                                                                                '&:hover': {
                                                                                    background: '#555'
                                                                                }
                                                                            },
                                                                            '& .MuiMenuItem-root': {
                                                                                fontSize: '0.875rem',
                                                                                padding: '12px 16px',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(13, 83, 189, 0.1)'
                                                                                },
                                                                                '&.Mui-selected': {
                                                                                    backgroundColor: '#0d53bd',
                                                                                    color: 'white',
                                                                                    '&:hover': {
                                                                                        backgroundColor: '#0d53bd'
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>None</em>
                                                                </MenuItem>
                                                                <MenuItem value="<1k">Less than ₹1K</MenuItem>
                                                                <MenuItem value="1-5k">₹1K - ₹5K</MenuItem>
                                                                <MenuItem value="5-10k">₹5K - ₹10K</MenuItem>
                                                                <MenuItem value="10-25k">₹10K - ₹25K</MenuItem>
                                                                <MenuItem value="25-50k">₹25K - ₹50K</MenuItem>
                                                                <MenuItem value="50-100k">₹50K - ₹100K</MenuItem>
                                                                <MenuItem value="100-250k">₹100K - ₹250K</MenuItem>
                                                                <MenuItem value="250-500k">₹250K - ₹500K</MenuItem>
                                                                <MenuItem value="500k-1m">₹500K - ₹1M</MenuItem>
                                                                <MenuItem value="1-5m">₹1M - ₹5M</MenuItem>
                                                                <MenuItem value=">5m">More than ₹5M</MenuItem>
                                                                <MenuItem value="uncertain">Uncertain / Not sure</MenuItem>
                                                                <MenuItem value="other">Other</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold fs-6 text-primary">Launch Timeline 🗓️</label>
                                                <div className="position-relative">
                                                    {form.timeline === 'other' ? (
                                                        <div className="d-flex align-items-center">
                                                            <input
                                                                ref={timelineOtherRef} // Ref add karo
                                                                className="form-control form-control-lg border-0 fs-6"
                                                                name="timelineOther"
                                                                value={form.timelineOther || ''}
                                                                onChange={(e) => setForm({ ...form, timelineOther: e.target.value })}
                                                                placeholder="e.g. 18 months, 2 years"
                                                                style={{
                                                                    background: '#f8f9fa',
                                                                    borderRadius: 12,
                                                                    paddingRight: '50px',
                                                                    border: '1px solid rgba(0,0,0,0.1)',
                                                                    transition: 'all 0.3s ease'
                                                                }}
                                                                onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                                                onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-link position-absolute end-0"
                                                                onClick={() => setForm({ ...form, timeline: '', timelineOther: '' })}
                                                                style={{
                                                                    padding: '8px 16px',
                                                                    color: '#666',
                                                                    textDecoration: 'none',
                                                                    fontSize: '1.5rem',
                                                                    transition: 'all 0.3s ease',
                                                                    zIndex: 2
                                                                }}
                                                                title="Back to options"
                                                                onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                                                onMouseLeave={(e) => e.target.style.color = '#666'}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <FormControl fullWidth variant="outlined" size="medium">
                                                            <InputLabel
                                                                id="timeline-select-label"
                                                                sx={{
                                                                    color: '#6c757d',
                                                                    fontSize: '0.875rem',
                                                                    '&.Mui-focused': {
                                                                        color: '#0d53bd'
                                                                    }
                                                                }}
                                                            >
                                                                Select timeline
                                                            </InputLabel>
                                                            <Select
                                                                labelId="timeline-select-label"
                                                                id="timeline-select"
                                                                name="timeline"
                                                                value={form.timeline || ''}
                                                                onChange={handleChange}
                                                                label="Select timeline"
                                                                sx={{
                                                                    borderRadius: '12px',
                                                                    background: '#f8f9fa',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: 'rgba(0,0,0,0.1)',
                                                                        borderRadius: '12px'
                                                                    },
                                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#0d53bd'
                                                                    },
                                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: '#0d53bd',
                                                                        borderWidth: '2px'
                                                                    },
                                                                    '& .MuiSelect-select': {
                                                                        padding: '15.5px 14px',
                                                                        fontSize: '0.875rem',
                                                                        display: 'flex',
                                                                    }
                                                                }}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        sx: {
                                                                            borderRadius: '12px',
                                                                            marginTop: '8px',
                                                                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                                            maxHeight: 250,
                                                                            '&::-webkit-scrollbar': {
                                                                                width: '8px',
                                                                            },
                                                                            '&::-webkit-scrollbar-track': {
                                                                                background: '#f1f1f1',
                                                                                borderRadius: '4px',
                                                                            },
                                                                            '&::-webkit-scrollbar-thumb': {
                                                                                background: '#888',
                                                                                borderRadius: '4px',
                                                                                '&:hover': {
                                                                                    background: '#555'
                                                                                }
                                                                            },
                                                                            '& .MuiMenuItem-root': {
                                                                                fontSize: '0.875rem',
                                                                                padding: '12px 16px',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(13, 83, 189, 0.1)'
                                                                                },
                                                                                '&.Mui-selected': {
                                                                                    backgroundColor: '#0d53bd',
                                                                                    color: 'white',
                                                                                    '&:hover': {
                                                                                        backgroundColor: '#0d53bd'
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <MenuItem value="">
                                                                    <em>None</em>
                                                                </MenuItem>
                                                                <MenuItem value="immediate">Immediately</MenuItem>
                                                                <MenuItem value="1-3">1-3 months</MenuItem>
                                                                <MenuItem value="3-6">3-6 months</MenuItem>
                                                                <MenuItem value="6-12">6-12 months</MenuItem>
                                                                <MenuItem value="12-18">12-18 months</MenuItem>
                                                                <MenuItem value="18-24">18-24 months</MenuItem>
                                                                <MenuItem value="2-3">2-3 years</MenuItem>
                                                                <MenuItem value="3-5">3-5 years</MenuItem>
                                                                <MenuItem value=">5">More than 5 years</MenuItem>
                                                                <MenuItem value="flexible">Flexible / No fixed timeline</MenuItem>
                                                                <MenuItem value="other">Other</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {step === 3 && (
                                        <div className="row g-4">
                                            <div className="col-12">
                                                <h5 className="fw-bold mb-4 text-primary">📋 Review Your Business Idea</h5>
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <div className="p-3 rounded-3" style={{
                                                            background: '#f8f9fa',
                                                            border: '1px solid rgba(0,0,0,0.1)',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            <strong>🎯 Idea:</strong> {form.idea || 'Not specified'}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 rounded-3" style={{
                                                            background: '#f8f9fa',
                                                            border: '1px solid rgba(0,0,0,0.1)',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            <strong>🏢 Industry:</strong> {form.industry === 'other' ? form.industryOther :
                                                                (form.industry === 'tech' ? 'Technology & SaaS' :
                                                                    form.industry === 'food' ? 'Food & Beverage' :
                                                                        form.industry === 'health' ? 'Health & Wellness' :
                                                                            form.industry === 'finance' ? 'Finance & Fintech' :
                                                                                form.industry === 'education' ? 'Education & EdTech' :
                                                                                    form.industry === 'ecommerce' ? 'E-commerce & Retail' :
                                                                                        form.industry === 'entertainment' ? 'Entertainment & Media' :
                                                                                            form.industry === 'realestate' ? 'Real Estate & Property' :
                                                                                                form.industry === 'transportation' ? 'Transportation & Logistics' :
                                                                                                    form.industry === 'manufacturing' ? 'Manufacturing' :
                                                                                                        form.industry === 'agriculture' ? 'Agriculture & Farming' :
                                                                                                            form.industry === 'hospitality' ? 'Hospitality & Tourism' :
                                                                                                                form.industry === 'fashion' ? 'Fashion & Apparel' :
                                                                                                                    form.industry === 'beauty' ? 'Beauty & Cosmetics' :
                                                                                                                        form.industry === 'sports' ? 'Sports & Fitness' :
                                                                                                                            form.industry === 'automotive' ? 'Automotive' :
                                                                                                                                form.industry === 'energy' ? 'Energy & Utilities' :
                                                                                                                                    form.industry === 'consulting' ? 'Consulting & Services' :
                                                                                                                                        form.industry === 'nonprofit' ? 'Non-profit & Social Impact' :
                                                                                                                                            'Not specified')}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 rounded-3" style={{
                                                            background: '#f8f9fa',
                                                            border: '1px solid rgba(0,0,0,0.1)',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            <strong>👥 Audience:</strong> {form.audience || 'Not specified'}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 rounded-3" style={{
                                                            background: '#f8f9fa',
                                                            border: '1px solid rgba(0,0,0,0.1)',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            <strong>🔧 Problem:</strong> {form.problem || 'Not specified'}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 rounded-3" style={{
                                                            background: '#f8f9fa',
                                                            border: '1px solid rgba(0,0,0,0.1)',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            <strong>💰 Budget:</strong> {form.budget === 'other' ? form.budgetOther :
                                                                (form.budget === '<1k' ? 'Less than ₹1K' :
                                                                    form.budget === '1-5k' ? '₹1K - ₹5K' :
                                                                        form.budget === '5-10k' ? '₹5K - ₹10K' :
                                                                            form.budget === '10-25k' ? '₹10K - ₹25K' :
                                                                                form.budget === '25-50k' ? '₹25K - ₹50K' :
                                                                                    form.budget === '50-100k' ? '₹50K - ₹100K' :
                                                                                        form.budget === '100-250k' ? '₹100K - ₹250K' :
                                                                                            form.budget === '250-500k' ? '₹250K - ₹500K' :
                                                                                                form.budget === '500k-1m' ? '₹500K - ₹1M' :
                                                                                                    form.budget === '1-5m' ? '₹1M - ₹5M' :
                                                                                                        form.budget === '>5m' ? 'More than ₹5M' :
                                                                                                            form.budget === 'uncertain' ? 'Uncertain / Not sure' :
                                                                                                                'Not specified')}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 rounded-3" style={{
                                                            background: '#f8f9fa',
                                                            border: '1px solid rgba(0,0,0,0.1)',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            <strong>🗓️ Timeline:</strong> {form.timeline === 'other' ? form.timelineOther :
                                                                (form.timeline === 'immediate' ? 'Immediately' :
                                                                    form.timeline === '1-3' ? '1-3 months' :
                                                                        form.timeline === '3-6' ? '3-6 months' :
                                                                            form.timeline === '6-12' ? '6-12 months' :
                                                                                form.timeline === '12-18' ? '12-18 months' :
                                                                                    form.timeline === '18-24' ? '18-24 months' :
                                                                                        form.timeline === '2-3' ? '2-3 years' :
                                                                                            form.timeline === '3-5' ? '3-5 years' :
                                                                                                form.timeline === '>5' ? 'More than 5 years' :
                                                                                                    form.timeline === 'flexible' ? 'Flexible / No fixed timeline' :
                                                                                                        'Not specified')}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="alert alert-primary mt-4 border-0" style={{
                                                    background: 'rgba(13, 83, 189, 0.1)',
                                                    borderRadius: 12,
                                                    border: '1px solid rgba(13, 83, 189, 0.2)',
                                                    transition: 'all 0.3s ease'
                                                }}>
                                                    <strong>🚀 Ready for Analysis!</strong> Click "Generate AI Analysis" to get comprehensive business insights powered by artificial intelligence.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Navigation Buttons */}
                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                                    <button
                                        className="btn btn-outline-primary d-flex align-items-center order-2 order-sm-1 w-100 w-sm-auto justify-content-center"
                                        onClick={prevStep}
                                        disabled={step === 0}
                                        style={{
                                            borderRadius: 12,
                                            padding: '12px 24px',
                                            border: '1px solid #0d53bd',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#0d53bd';
                                            e.target.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                            e.target.style.color = '#0d53bd';
                                        }}
                                    >
                                        <ChevronLeft size={18} className="me-2" />
                                        Previous
                                    </button>

                                    {step < steps.length - 1 ? (
                                        <button
                                            className="btn btn-primary d-flex align-items-center order-1 order-sm-2 w-100 w-sm-auto justify-content-center"
                                            onClick={nextStep}
                                            disabled={
                                                (step === 0 && (!form.idea || !form.industry ||
                                                    (form.industry === 'other' && !form.industryOther))) ||
                                                (step === 1 && (!form.audience || !form.problem)) ||
                                                (step === 2 && (!form.budget || !form.timeline ||
                                                    (form.budget === 'other' && !form.budgetOther) ||
                                                    (form.timeline === 'other' && !form.timelineOther)))
                                            }
                                            style={{
                                                borderRadius: 12,
                                                padding: '12px 32px',
                                                background: '#0d53bd',
                                                border: 'none',
                                                transition: 'all 0.3s ease',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = 'linear-gradient(90deg, #0d53bd, #1a6ae8)';
                                                e.target.style.transform = 'translateY(-2px)';
                                                e.target.style.boxShadow = '0 8px 20px rgba(13, 83, 189, 0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = '#0d53bd';
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        >
                                            Next
                                            <ChevronRight size={18} className="ms-2" />
                                        </button>
                                    ) : (
                                        <button
                                            className="btn btn-success d-flex align-items-center order-1 order-sm-2 w-100 w-sm-auto justify-content-center"
                                            onClick={generateAnalysis}
                                            disabled={loading}
                                            style={{
                                                borderRadius: 12,
                                                padding: '12px 32px',
                                                background: 'linear-gradient(90deg, #10b981, #06b6d4)',
                                                border: 'none',
                                                transition: 'all 0.3s ease',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!loading) {
                                                    e.target.style.transform = 'translateY(-2px)';
                                                    e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm me-2" />
                                                    Analyzing...
                                                </>
                                            ) : (
                                                <>
                                                    <Rocket size={18} className="me-2" />
                                                    Generate AI Analysis
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    /* Analysis Results */
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <div className="container-fluid px-0 mb-4 mb-md-5">
                                <div className="row align-items-center g-2">
                                    {/* Back Button Column */}
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-outline-primary d-flex align-items-center justify-content-center order-1 order-md-0"
                                            onClick={() => window.location.href = '/'}
                                            style={{
                                                borderRadius: '12px',
                                                padding: 'clamp(8px, 2vw, 10px) clamp(12px, 2.5vw, 20px)',
                                                fontSize: 'clamp(14px, 3vw, 16px)',
                                                height: 'clamp(40px, 8vw, 44px)',
                                                minWidth: 'clamp(40px, 10vw, 120px)',
                                                flexShrink: 0,
                                                borderWidth: '2px',
                                                transition: 'all 0.3s ease',
                                                alignSelf: 'flex-start'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#0d53bd';
                                                e.currentTarget.style.color = 'white';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.color = '#0d53bd';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            {/* XL Screens (≥1200px): Full text with icon */}
                                            <span className="d-none d-xl-inline-flex align-items-center">
                                                <FaCircleArrowLeft className="me-2" size={18} />
                                                Back to Home
                                            </span>

                                            {/* LG Screens (992px-1199px): Icon + "Back" */}
                                            <span className="d-none d-lg-inline-flex d-xl-none align-items-center">
                                                <FaCircleArrowLeft className="me-1" size={16} />
                                                Back
                                            </span>

                                            {/* MD Screens (768px-991px): Only icon with text */}
                                            <span className="d-none d-md-inline-flex d-lg-none align-items-center">
                                                <FaCircleArrowLeft size={16} />
                                                <span className="ms-1">Back</span>
                                            </span>

                                            {/* SM Screens (576px-767px): Only icon */}
                                            <span className="d-none d-sm-inline-flex d-md-none align-items-center justify-content-center"
                                                title="Back to Home">
                                                <FaCircleArrowLeft size={16} />
                                            </span>

                                            {/* XS Screens (<576px): Only icon, centered */}
                                            <span className="d-inline-flex d-sm-none align-items-center justify-content-center"
                                                title="Back to Home">
                                                <FaCircleArrowLeft size={16} />
                                            </span>
                                        </button>
                                    </div>

                                    {/* Title Column - Takes remaining space */}
                                    <div className="col">
                                        <div className="text-center">
                                            <h1 className="fw-bold mb-1" style={{
                                                background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                fontSize: 'clamp(1.3rem, 3.8vw, 1.9rem)',
                                                lineHeight: '1.2',
                                                wordBreak: 'break-word',
                                                padding: '0 10px'
                                            }}>
                                                Your AI-Powered Business Analysis
                                            </h1>

                                            <p className="text-muted m-0" style={{ fontSize: 'clamp(0.85rem, 1.6vw, 0.95rem)' }}>
                                                Comprehensive insights for : <strong style={{ color: '#0d53bd' }}>"{form.businessName || 'Your Business'}"</strong>
                                            </p>
                                        </div>
                                    </div>

                                    {/* Empty column for balance on larger screens */}
                                    <div className="col-auto d-none d-lg-block" style={{ width: '140px' }}></div>
                                </div>
                            </div>

                            <div className="row g-4">
                                {analysisSections.map((section, idx) => (
                                    <div key={idx} className="col-12 col-md-6 col-lg-4">
                                        <SectionCard section={section} analysis={analysis} />
                                    </div>
                                ))}
                            </div>

                            <div className="text-center mt-4 mt-md-5">
                                <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                                    <button
                                        className="btn btn-outline-primary fs-6"
                                        onClick={() => setAnalysis(null)}
                                        style={{ borderRadius: 12, padding: '12px 24px' }}
                                    >
                                        ← Back to Form
                                    </button>
                                    <button
                                        className="btn btn-primary fs-6"
                                        onClick={() => {
                                            setAnalysis(null);
                                            setForm({
                                                idea: '',
                                                industry: '',
                                                audience: '',
                                                problem: '',
                                                budget: '',
                                                timeline: ''
                                            });
                                            setStep(0);
                                        }}
                                        style={{ borderRadius: 12, padding: '12px 24px' }}
                                    >
                                        Start New Analysis
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal for expanded view */}
                {selectedSection && (
                    <SectionModal
                        section={selectedSection}
                        analysis={analysis}
                        onClose={() => setSelectedSection(null)}
                    />
                )}
            </div>
        </div>
    );
}

export default New;
